<script setup lang="ts">
import { useId } from "vue";

/**
 * 켜기/끄기 스위치(`role="switch"` · `aria-checked`) — 바로 적용되는 설정 하나(알림 받기 · 공개 여부)에 쓴다.
 * - 저장 버튼을 눌러야 반영되는 폼 값이면 체크박스가 맞다. 스위치는 "누르는 즉시 켜짐/꺼짐"으로 읽힌다.
 * - 라벨은 버튼 안 글자라 누를 대상이 라벨까지 넓다. 설명(`description`)은 aria-describedby 로 잇는다.
 * - 모양: 트랙 36×20 · 손잡이 16, 켜짐 = 앱 주 파랑. 동작 줄이기 설정이면 미끄러짐 없이 바뀐다(전역 reduced-motion).
 * - 잠김(`disabled`)은 네이티브 `disabled` 가 아니라 `aria-disabled="true"` + 클릭 차단(73차 후반 리뷰 RV-FA-01 — 저장 중 모든
 *   스위치가 잠기며 포커스가 body 로 떨어졌다). `LoadingButton` · `IconButton` 과 같은 규칙, 모양은 그대로.
 */
const props = withDefaults(
  defineProps<{
    /** 스위치 이름(보이는 라벨) */
    label: string;
    /** 라벨 아래 보조 설명 */
    description?: string;
    /** 잠금 */
    disabled?: boolean;
    /**
     * 접근 이름(보이는 label 은 그대로 표시). 표 행마다 같은 라벨의 스위치를 구분할 때 쓴다. 음성 입력 사용자가 보이는 글자로 부를 수
     * 있게 보이는 label 로 시작한다(WCAG 2.5.3) — 예: 「키트 반영 — 홍길동」. 없으면 label 이 이름이다.
     */
    ariaLabel?: string;
  }>(),
  { description: undefined, disabled: false, ariaLabel: undefined },
);

const model = defineModel<boolean>({ default: false });
const descriptionId = useId();

function toggle(): void {
  if (!props.disabled) model.value = !model.value;
}
</script>

<template>
  <div class="ui-switch-field">
    <button
      type="button"
      role="switch"
      class="ui-switch"
      :aria-checked="model"
      :aria-label="ariaLabel"
      :aria-describedby="description ? descriptionId : undefined"
      :aria-disabled="disabled ? 'true' : undefined"
      @click="toggle"
    >
      <span class="ui-switch-track" aria-hidden="true">
        <span class="ui-switch-thumb" />
      </span>
      <span class="ui-switch-label">{{ label }}</span>
    </button>
    <p v-if="description" :id="descriptionId" class="ui-switch-desc">
      {{ description }}
    </p>
  </div>
</template>

<style scoped>
.ui-switch-field {
  display: grid;
  gap: var(--ui-gap-2xs);
  min-width: 0;
}

/* 버튼 기본 모양(테두리 · 면 · 그림자)을 걷고 [트랙 · 라벨] 한 줄로 둔다. 터치 대상 높이 36. */
.ui-switch {
  display: inline-flex;
  align-items: center;
  gap: var(--ui-gap-sm);
  justify-self: start;
  min-height: var(--ui-h-btn-sm);
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: var(--ui-radius-sm);
  background: none;
  box-shadow: none;
  color: var(--ui-text);
  font: inherit;
  font-size: var(--ui-fs-md);
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

/*
 * 꺼짐 트랙은 `--ui-switch-off`(UXS81-08 — 옛 연회색 트랙 + 흰 손잡이는 흰 면 대비 1.5:1 이라 「빈 알약」 이었다. WCAG 1.4.11 비텍스트 3:1).
 * 손잡이는 트랙 위에 떠 보이도록 그림자를 준다. 켜짐은 그대로.
 */
.ui-switch-track {
  --ui-switch-off: var(--ui-text-3);

  position: relative;
  flex: none;
  width: 36px;
  height: 20px;
  border: 1px solid var(--ui-switch-off);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-switch-off);
  transition:
    background-color var(--ui-dur-fast) var(--ui-ease),
    border-color var(--ui-dur-fast) var(--ui-ease);
}

.ui-switch-thumb {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--ui-surface);
  box-shadow: 0 1px 3px rgb(15 23 42 / 35%);
  transition: transform var(--ui-dur-fast) var(--ui-ease);
}

.ui-switch[aria-checked="true"] .ui-switch-track {
  border-color: var(--ui-blue-600);
  background: var(--ui-blue-600);
}

.ui-switch[aria-checked="true"] .ui-switch-thumb {
  transform: translateX(16px);
}

.ui-switch:hover:not(:disabled, [aria-disabled="true"]) .ui-switch-track {
  border-color: var(--ui-text-2);
  background: var(--ui-text-2);
}

.ui-switch[aria-checked="true"]:hover:not(:disabled, [aria-disabled="true"])
  .ui-switch-track {
  border-color: var(--ui-blue-700);
  background: var(--ui-blue-700);
}

.ui-switch:is(:disabled, [aria-disabled="true"]) {
  color: var(--ui-disabled);
  cursor: not-allowed;
}

.ui-switch:is(:disabled, [aria-disabled="true"]) .ui-switch-track {
  border-color: var(--ui-input-line);
  background: var(--ui-line-strong);
}

.ui-switch[aria-checked="true"]:is(:disabled, [aria-disabled="true"])
  .ui-switch-track {
  border-color: var(--ui-blue-300);
  background: var(--ui-blue-300);
}

/* 설명은 라벨 글자 왼쪽 끝(트랙 36 + 간격 8)에 맞춘다. */
.ui-switch-desc {
  margin: 0;
  padding-left: calc(36px + var(--ui-gap-sm));
  color: var(--ui-text-2);
  font-size: var(--ui-fs-sm);
  line-height: 1.5;
  word-break: keep-all;
  overflow-wrap: break-word;
}
</style>
